"use client";

import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

export const COOKIE_CONSENT_KEY = "qk-cookie-consent";
export const COOKIE_CONSENT_EVENT = "qk-cookie-consent-change";
export const CONSENT_MODE_COOKIE = "qk-consent-mode";
// Settings dispatches this to reopen the dialog for a logged-in user, since
// the persistent pill is hidden once authenticated (see the render guard
// below) — GDPR/ePrivacy still requires the choice stay revisitable.
export const COOKIE_PREFERENCES_OPEN_EVENT = "qk-open-cookie-preferences";

// "optin" (EU/EEA/UK/CH: ask first) or "optout" (elsewhere: analytics on by
// default, pill to turn off). Set by middleware.js from the request country;
// missing cookie (local dev, storage blocked) falls back to opt-in.
export function getConsentMode() {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_MODE_COOKIE}=(optin|optout)`));
    return match ? match[1] : "optin";
  } catch {
    return "optin";
  }
}

export default function CookieConsent() {
  const { isAuthenticated } = useAuth0();
  const [choice, setChoice] = useState(null);
  const [mode, setMode] = useState("optin");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const consentMode = getConsentMode();
    setMode(consentMode);
    try {
      const saved = window.localStorage.getItem(COOKIE_CONSENT_KEY);
      setChoice(saved);
      setOpen(consentMode === "optin" && saved !== "analytics" && saved !== "essential");
    } catch {
      setOpen(consentMode === "optin");
    }
  }, []);

  // Settings' "Cookie preferences" reopens the dialog for a logged-in user,
  // who never sees the persistent pill (see the render guard below).
  useEffect(() => {
    const openDialog = () => setOpen(true);
    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, openDialog);
    return () => window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, openDialog);
  }, []);

  const choose = (nextChoice) => {
    let previousChoice = null;
    try {
      previousChoice = window.localStorage.getItem(COOKIE_CONSENT_KEY);
      window.localStorage.setItem(COOKIE_CONSENT_KEY, nextChoice);
    } catch {
      // The in-memory choice still applies for this page when storage is blocked.
    }
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: nextChoice }));
    setChoice(nextChoice);
    setOpen(false);
    // Reload to unload already-running analytics scripts when the effective
    // state goes from on (explicit accept, or opt-out region default) to off.
    const wasEffectivelyOn = previousChoice === "analytics" || (previousChoice !== "essential" && mode === "optout");
    if (nextChoice === "essential" && wasEffectivelyOn) window.location.reload();
  };

  // The dialog itself always renders when explicitly open (fresh opt-in
  // visitor being asked for the first time, or Settings requesting a
  // revisit) — auth state only ever suppresses the collapsed PILL.
  if (!open) {
    // Logged-in users reach this via Settings instead of a pill floating
    // over their manuscript; the choice stays revisitable either way.
    if (isAuthenticated) return null;
    // In opt-out regions the pill is always available so analytics can be declined.
    if (!choice && mode !== "optout") return null;
  }

  return (
    <div style={{ position: "fixed", left: 16, bottom: 16, zIndex: 10000, fontFamily: "Satoshi, system-ui, sans-serif" }}>
      {open ? (
        <section
          role="dialog"
          aria-label="Cookie preferences"
          style={{ width: "min(420px, calc(100vw - 32px))", padding: 20, border: "1.5px solid var(--qk-ink)", borderRadius: 14, background: "var(--qk-paper-3)", color: "var(--qk-ink)", boxShadow: "6px 6px 0 var(--qk-ink)" }}
        >
          <h2 style={{ margin: 0, fontFamily: "Gambarino, Georgia, serif", fontSize: 24, fontWeight: 400 }}>your privacy choices.</h2>
          <p style={{ margin: "8px 0 16px", fontSize: 14, lineHeight: 1.5 }}>
            {mode === "optout"
              ? "Quarkle uses essential storage to run the service, plus analytics that help us understand usage and measure referrals. You can turn analytics off any time."
              : "Quarkle uses essential storage to run the service. With your permission, analytics help us understand usage and measure referrals. You can change this choice any time."}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <button type="button" onClick={() => choose("analytics")} style={{ cursor: "pointer", border: "1.5px solid var(--qk-ink)", borderRadius: 999, padding: "9px 14px", background: "var(--qk-pink)", color: "var(--qk-on-pink)", fontWeight: 700 }}>
              accept analytics
            </button>
            <button type="button" onClick={() => choose("essential")} style={{ cursor: "pointer", border: "1.5px solid var(--qk-ink)", borderRadius: 999, padding: "9px 14px", background: "transparent", color: "var(--qk-ink)", fontWeight: 700 }}>
              essential only
            </button>
            <a href="/privacy#cookies" style={{ alignSelf: "center", color: "var(--qk-ink)", fontSize: 12, textUnderlineOffset: 3 }}>privacy policy</a>
          </div>
        </section>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ cursor: "pointer", border: "1px solid color-mix(in srgb, var(--qk-ink) 35%, transparent)", borderRadius: 999, padding: "7px 11px", background: "var(--qk-paper-3)", color: "var(--qk-ink)", boxShadow: "2px 2px 0 color-mix(in srgb, var(--qk-ink) 28%, transparent)", fontSize: 11, fontWeight: 700 }}
        >
          privacy choices
        </button>
      )}
    </div>
  );
}
